/**
 * One sheet, open for editing.
 *
 * Loads from the server and autosaves back to it. The unverified marker lives here
 * rather than inside the editor, so it sits in the page header at every viewport and
 * cannot be scrolled away, collapsed into a pane, or restyled by anything the author
 * does (1.3.2).
 */
import { useEffect, useMemo, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Check, ChevronLeft, CloudOff, Loader2, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SheetEditor } from '@/sheets/components/SheetEditor'
import { useSheet } from '@/sheets/useSheet'
import { useSheetSync } from '@/sheets/useSheetSync'
import type { SyncStatus } from '@/sheets/useSheetSync'
import { getSheet, readDraft } from '@/sheets/api'
import { PublishControl } from '@/sheets/components/PublishControl'
import { TemplateControl } from '@/sheets/components/TemplateControl'
import { FullPageSpinner } from '@/components/layout/FullPageSpinner'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { NotFoundPage } from '@/pages/NotFoundPage'

const SYNC_ICON: Record<SyncStatus, typeof Check> = {
  idle: Check,
  unsaved: CloudOff,
  saving: Loader2,
  saved: Check,
  conflict: AlertTriangle,
  error: CloudOff,
}

function SyncBadge({ status }: { status: SyncStatus }) {
  const { t } = useTranslation('sheets')
  const Icon = SYNC_ICON[status]
  const tone =
    status === 'conflict' || status === 'error' ? 'text-[var(--status-warn)]' : 'text-text-muted'

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${tone}`} data-testid="sync-status">
      <Icon className={`h-3.5 w-3.5 ${status === 'saving' ? 'animate-spin' : ''}`} aria-hidden />
      <span className="hidden sm:inline">{t(`sheets.sync.${status}`)}</span>
    </span>
  )
}

export function SheetPage() {
  const { sheetId } = useParams<{ sheetId: string }>()
  const id = Number(sheetId)

  const query = useQuery({
    queryKey: ['sheet', id],
    queryFn: ({ signal }) => getSheet(id, signal),
    enabled: Number.isFinite(id),
    // The editor owns the document once it is open; refetching underneath it would
    // discard what the engineer has typed.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  // An unsaved draft outranks the server copy: it is strictly newer, and it exists
  // precisely because the last save did not land.
  const initial = useMemo(() => {
    if (!query.data) return null
    const draft = readDraft(id)
    return draft && draft.baseRowVersion === query.data.row_version
      ? draft.document
      : query.data.document
  }, [id, query.data])

  if (query.isLoading) return <FullPageSpinner />
  if (query.isError || !query.data || !initial) return <NotFoundPage />

  return (
    <SheetWorkspace
      id={id}
      initial={initial}
      rowVersion={query.data.row_version}
      canEdit={query.data.can_edit}
      publishedVersion={query.data.published_version}
      hasUnpublishedChanges={query.data.has_unpublished_changes}
      onPublished={() => void query.refetch()}
    />
  )
}

/**
 * Split from the page so the hooks below run only once the document has arrived --
 * `useSheet` seeds state from its argument, so mounting it with a placeholder and
 * swapping in the real document later would strand the editor on the placeholder.
 */
function SheetWorkspace({
  id,
  initial,
  rowVersion,
  canEdit,
  publishedVersion,
  hasUnpublishedChanges,
  onPublished,
}: {
  id: number
  initial: Parameters<typeof useSheet>[0]
  rowVersion: number
  canEdit: boolean
  publishedVersion: number | null
  hasUnpublishedChanges: boolean
  onPublished: () => void
}) {
  const { t } = useTranslation('sheets')
  const sheet = useSheet(initial, { persistDrafts: false })
  const sync = useSheetSync(id, sheet.doc, rowVersion, { enabled: canEdit })

  // A save still debouncing when the tab closes is a save that never happened. The
  // handler is read through a ref so this effect mounts once: depending on `saveNow`
  // directly would re-register on every save and flush from its cleanup each time,
  // which is an infinite save loop rather than a tidy-up.
  const flushRef = useRef(sync.saveNow)
  useEffect(() => {
    flushRef.current = sync.saveNow
  }, [sync.saveNow])

  useEffect(() => {
    const flush = () => void flushRef.current()
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [])

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text">
      <a
        href="#sheet-main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface-raised focus:px-3 focus:py-2"
      >
        {t('sheets.title')}
      </a>

      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-3 py-2 sm:px-4">
        <Link
          to="/sheets"
          className="flex h-11 w-11 items-center justify-center rounded-md text-text-muted hover:text-text"
          aria-label={t('sheets.title')}
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </Link>

        <input
          value={sheet.doc.title}
          onChange={(event) => sheet.rename(event.target.value)}
          readOnly={!canEdit}
          aria-label={t('sheets.action.rename')}
          placeholder={t('sheets.untitled')}
          className="min-w-0 flex-1 border-0 bg-transparent text-base font-semibold text-text placeholder:text-text-muted focus:outline-none"
        />

        <SyncBadge status={sync.status} />

        <PublishControl
          sheetId={id}
          publishedVersion={publishedVersion}
          hasUnpublishedChanges={hasUnpublishedChanges}
          canEdit={canEdit}
          onPublished={onPublished}
          onBeforePublish={sync.saveNow}
        />

        <TemplateControl sheetId={id} defaultTitle={sheet.doc.title} onBeforeSave={sync.saveNow} />

        {/* Persistent, and the author can neither remove nor restyle it (1.3.2). */}
        <span
          className="inline-flex shrink-0 items-center gap-2 rounded-md border border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--status-warn)]"
          data-testid="unverified-marker"
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">{t('sheets.unverified.badge')}</span>
          <Lock className="h-3 w-3 opacity-70" aria-hidden />
        </span>

        <div className="flex shrink-0 items-center gap-1">
          <ThemeToggle />
          <LanguageToggle />
        </div>
      </header>

      {sync.status === 'conflict' && (
        <div
          role="alert"
          className="flex items-center gap-2 border-b border-[var(--status-warn)] bg-[var(--status-warn-bg)] px-4 py-2 text-sm text-[var(--status-warn)]"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          {t('sheets.sync.conflict_detail')}
        </div>
      )}

      <main id="sheet-main" className="flex min-h-0 flex-1 flex-col">
        <SheetEditor sheet={sheet} />
      </main>
    </div>
  )
}
